import {useEffect} from 'react';

import PropTypes from 'prop-types';

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui';

import {
  BNPL_BREAKPOINT_LG,
  BNPL_PAGE_TYPE,
  buildAfterpayShadowStyles,
} from '@amblique/king-living/app/constants/bnpl-messaging';
import {useAfterpayScript} from '@amblique/king-living/app/hooks/use-bnpl-script';

const AFTERPAY_STYLE_ID = 'king-bnpl-afterpay-icon';
const AFTERPAY_STYLE_SYNC_MS = 500;

const syncAfterpayShadowStyles = (placementEl, isDesktop) => {
  const shadow = placementEl?.shadowRoot;

  if (!shadow) {
    return;
  }

  const css = buildAfterpayShadowStyles(isDesktop);
  let style = shadow.getElementById(AFTERPAY_STYLE_ID);

  if (!style) {
    style = document.createElement('style');
    style.id = AFTERPAY_STYLE_ID;
    shadow.appendChild(style);
  } else if (style.textContent === css) {
    return;
  }

  style.textContent = css;
};

const AfterpayMessaging = ({
  amount,
  config,
  currency,
  isEligible = true,
  locale,
  pageType,
  styles,
}) => {
  const {mpid, platform, scriptMax, scriptMin, scriptUrl} = config;
  const isConfigured = Boolean(mpid && amount);
  const placementId = `afterpay-cta-${pageType === BNPL_PAGE_TYPE.CART ? 'cart' : 'pdp'}`;

  const {isReady} = useAfterpayScript(isConfigured, {url: scriptUrl, min: scriptMin, max: scriptMax});

  useEffect(() => {
    if (!isConfigured || !isReady) {
      return undefined;
    }

    let cancelled = false;
    const mediaQuery = window.matchMedia(`(min-width: ${BNPL_BREAKPOINT_LG})`);

    const applyStyles = () => {
      if (!cancelled) {
        syncAfterpayShadowStyles(document.getElementById(placementId), mediaQuery.matches);
      }
    };

    applyStyles();

    const timer = window.setInterval(applyStyles, AFTERPAY_STYLE_SYNC_MS);

    mediaQuery.addEventListener('change', applyStyles);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      mediaQuery.removeEventListener('change', applyStyles);
    };
  }, [amount, isConfigured, isReady, placementId]);

  if (!isConfigured) {
    return null;
  }

  const eligibilityAttrs =
    pageType === BNPL_PAGE_TYPE.PRODUCT
      ? {'data-is-eligible': isEligible ? 'true' : 'false'}
      : {'data-cart-is-eligible': isEligible ? 'true' : 'false'};

  return (
    <Box className="bnpl-messaging__afterpay" sx={styles?.afterpayWidget}>
      <square-placement
        id={placementId}
        data-size="xs"
        data-show-lower-limit="true"
        data-currency={currency}
        data-consumer-locale={locale}
        data-amount={amount}
        data-mpid={mpid}
        data-cbt-enabled="true"
        data-platform={platform}
        data-page-type={pageType}
        {...eligibilityAttrs}
      />
    </Box>
  );
};

AfterpayMessaging.displayName = 'AfterpayMessaging';
AfterpayMessaging.propTypes = {
  amount: PropTypes.string,
  config: PropTypes.shape({
    mpid: PropTypes.string,
    platform: PropTypes.string,
    scriptMax: PropTypes.string,
    scriptMin: PropTypes.string,
    scriptUrl: PropTypes.string,
  }).isRequired,
  currency: PropTypes.string.isRequired,
  isEligible: PropTypes.bool,
  locale: PropTypes.string.isRequired,
  pageType: PropTypes.oneOf([BNPL_PAGE_TYPE.PRODUCT, BNPL_PAGE_TYPE.CART]).isRequired,
  styles: PropTypes.object,
};

export default AfterpayMessaging;
